/**
 * ProjectCard - Card "Projeto" da sidebar T1.
 *
 * Só renderiza quando `ticket.projectId` existe — usa a mesma rota
 * cross-module (`useProjectsDropdown`) já consumida pelo `TicketDetailHeader`
 * pro badge de projeto (mesma query key, sem duplicar fetch).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectsDropdown } from '@/modules/helpdesk/projects/hooks';
import { SidebarCard } from './SidebarCard';
import type { PopulatedTicket } from '../../types';

export interface ProjectCardProps {
  ticket: PopulatedTicket;
}

const PROJECT_STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  completed: 'Concluído',
  archived: 'Arquivado',
};

export const ProjectCard: React.FC<ProjectCardProps> = ({ ticket }) => {
  const navigate = useNavigate();
  const { data: projects } = useProjectsDropdown();

  const project = ticket.projectId ? projects?.find((p) => p.id === ticket.projectId) : undefined;

  if (!ticket.projectId || !project) return null;

  return (
    <SidebarCard icon="folder" title="Projeto" collapsible data-testid="ticket-detail-project-card">
      <button
        type="button"
        onClick={() => navigate(`/helpdesk/projects/${project.id}`)}
        className="w-full flex items-center gap-2 py-1.5 text-left transition-opacity hover:opacity-80"
        data-testid="ticket-detail-project-link"
      >
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
        <span className="flex-1 text-xs font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
          {project.name}
        </span>
        <span className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
          {PROJECT_STATUS_LABELS[project.status] || project.status}
        </span>
        <span className="text-[11px] font-medium" style={{ color: 'var(--theme-primary)' }}>
          abrir →
        </span>
      </button>
    </SidebarCard>
  );
};

ProjectCard.displayName = 'ProjectCard';
